'use client'

import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '@/store/hooks'
import { toast } from 'react-toastify'
import { MdArrowBack, MdSave, MdLocalPharmacy } from 'react-icons/md'
import {
    getPharmacyIntegration,
    createPharmacyIntegration,
    updatePharmacyIntegration,
    CreatePharmacyIntegrationData,
    PharmacyIntegrationType,
    PHARMACY_TYPE_LABELS,
} from '@/lib/api/health-integrations/pharmacy-api'

export default function PharmacyFormPage() {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const { user } = useAuth()
    const isEditing = !!id

    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)

    const [formData, setFormData] = useState<CreatePharmacyIntegrationData>({
        name: '',
        type: 'MEMED',
        apiUrl: '',
        apiKey: '',
        apiSecret: '',
        active: true,
        settings: {},
    })

    const [webhookUrl, setWebhookUrl] = useState('')

    useEffect(() => {
        if (isEditing && id) {
            loadIntegration(parseInt(id))
        }
    }, [id])

    const loadIntegration = async (integrationId: number) => {
        setLoading(true)
        try {
            const response = await getPharmacyIntegration(integrationId)
            if (response.success) {
                const integration = response.result
                setFormData({
                    name: integration.name,
                    type: integration.type,
                    apiUrl: integration.apiUrl || '',
                    apiKey: '',
                    apiSecret: '',
                    active: integration.active,
                    settings: integration.settings || {},
                })
                setWebhookUrl(integration.webhookUrl || '')
            } else {
                toast.error(response.error || 'Erro ao carregar integração')
                navigate('/health-integrations/pharmacy')
            }
        } catch (error) {
            toast.error('Erro ao carregar integração')
            navigate('/health-integrations/pharmacy')
        } finally {
            setLoading(false)
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!formData.name.trim()) {
            toast.error('Informe o nome da integração')
            return
        }

        setSaving(true)
        try {
            const data = { ...formData }
            if (!data.apiKey) {
                delete (data as any).apiKey
            }
            if (!data.apiSecret) {
                delete (data as any).apiSecret
            }

            const response = isEditing
                ? await updatePharmacyIntegration(parseInt(id!), data)
                : await createPharmacyIntegration(data)

            if (response.success) {
                toast.success(
                    `Integração ${isEditing ? 'atualizada' : 'criada'} com sucesso`
                )
                navigate('/health-integrations/pharmacy')
            } else {
                toast.error(
                    response.error ||
                    `Erro ao ${isEditing ? 'atualizar' : 'criar'} integração`
                )
            }
        } catch (error) {
            toast.error(
                `Erro ao ${isEditing ? 'atualizar' : 'criar'} integração`
            )
        } finally {
            setSaving(false)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando integração...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center gap-3">
                <button
                    onClick={() => navigate('/health-integrations/pharmacy')}
                    className="btn btn-ghost btn-sm"
                >
                    <MdArrowBack className="text-xl" />
                </button>
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shadow-lg">
                        <MdLocalPharmacy className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-base-content">
                            {isEditing ? 'Editar' : 'Nova'} Integração
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {isEditing
                                ? 'Atualize os dados da integração'
                                : 'Configure uma nova integração de receita eletrônica'}
                        </p>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h2 className="text-lg font-semibold mb-4">
                        Informações Básicas
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Nome <span className="text-error">*</span>
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full focus:input-primary"
                                placeholder="Ex: Memed Principal"
                                value={formData.name}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        name: e.target.value,
                                    })
                                }
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Tipo <span className="text-error">*</span>
                                </span>
                            </label>
                            <select
                                className="select select-bordered w-full focus:select-primary"
                                value={formData.type}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        type: e.target
                                            .value as PharmacyIntegrationType,
                                    })
                                }
                            >
                                {Object.entries(PHARMACY_TYPE_LABELS).map(
                                    ([value, label]) => (
                                        <option key={value} value={value}>
                                            {label}
                                        </option>
                                    )
                                )}
                            </select>
                        </div>
                    </div>

                    <div className="form-control mt-4">
                        <label className="label cursor-pointer justify-start gap-2">
                            <input
                                type="checkbox"
                                className="checkbox checkbox-secondary"
                                checked={formData.active}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        active: e.target.checked,
                                    })
                                }
                            />
                            <span className="label-text">Integração ativa</span>
                        </label>
                    </div>
                </div>

                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h2 className="text-lg font-semibold mb-4">
                        Configuração da API
                    </h2>
                    <div className="grid grid-cols-1 gap-4">
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    URL da API
                                </span>
                            </label>
                            <input
                                type="url"
                                className="input input-bordered w-full focus:input-primary"
                                placeholder="https://api.memed.com.br"
                                value={formData.apiUrl}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        apiUrl: e.target.value,
                                    })
                                }
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Chave da API (API Key)
                                    {isEditing && ' (deixe vazio para manter)'}
                                </span>
                            </label>
                            <input
                                type="password"
                                className="input input-bordered w-full focus:input-primary"
                                placeholder="Sua chave de API"
                                value={formData.apiKey}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        apiKey: e.target.value,
                                    })
                                }
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Segredo da API (API Secret)
                                    {isEditing && ' (deixe vazio para manter)'}
                                </span>
                            </label>
                            <input
                                type="password"
                                className="input input-bordered w-full focus:input-primary"
                                placeholder="Seu segredo de API"
                                value={formData.apiSecret}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        apiSecret: e.target.value,
                                    })
                                }
                            />
                        </div>
                    </div>
                </div>

                {isEditing && webhookUrl && (
                    <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                        <h2 className="text-lg font-semibold mb-4">
                            Webhook (Atualizações de Receita)
                        </h2>
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    URL do Webhook
                                </span>
                            </label>
                            <div className="join w-full">
                                <input
                                    type="text"
                                    className="input input-bordered join-item flex-1 focus:input-primary"
                                    value={webhookUrl}
                                    readOnly
                                />
                                <button
                                    type="button"
                                    className="btn btn-ghost join-item"
                                    onClick={() => {
                                        navigator.clipboard.writeText(webhookUrl)
                                        toast.success('URL copiada')
                                    }}
                                >
                                    Copiar
                                </button>
                            </div>
                            <label className="label">
                                <span className="label-text-alt">
                                    Configure esta URL na plataforma de receita
                                    eletrônica para receber atualizações de
                                    dispensação
                                </span>
                            </label>
                        </div>
                    </div>
                )}

                <div className="flex justify-end gap-2">
                    <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => navigate('/health-integrations/pharmacy')}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="btn btn-secondary gap-2"
                        disabled={saving}
                    >
                        {saving ? (
                            <span className="loading loading-spinner loading-sm"></span>
                        ) : (
                            <MdSave className="text-xl" />
                        )}
                        {isEditing ? 'Salvar' : 'Criar'}
                    </button>
                </div>
            </form>
        </div>
    )
}
